import { NAMED_COLORS, COLOR_CATEGORIES, COLOR_INTENSITIES, type Color } from "@corca/data";
import { mirrorColor } from "@corca/tiptap";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/react";

import { openListboxOnEnter } from "@/components/color-palette/openListboxOnEnter";
import { Sidebar } from "@/components/sidebar";
import { twMerge } from "@/utils/twMerge";

import { useCreateHintHoverHandlers } from "../hints/useHintHover";
import { type Props as ToggleButtonProps } from "../sidebar/ToggleButton";

import type { HintKey } from "@/store/HintsStore";

export type ColorPaletteColors = { lightThemeColor: string; darkThemeColor: string };

export function ColorButton({
  value,
  selected,
  colorClassName,
}: {
  value: string;
  selected: boolean;
  colorClassName: string;
}) {
  return (
    <ListboxOption
      value={value}
      aria-label={value}
      className={twMerge(
        "relative flex shrink-0 cursor-pointer",
        "after:hidden after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:w-9 after:h-9 after:bg-blue-100 dark:after:bg-blue-900",
        "hover:after:block",
        // The listbox keeps focus and marks the current option `data-focus`: highlight it like a
        // hovered one, with a ring while the keyboard drives it
        "data-[focus]:after:block",
        "in-focus-visible:data-[focus]:after:outline-solid in-focus-visible:data-[focus]:after:outline-2 in-focus-visible:data-[focus]:after:outline-blue-500",
      )}
    >
      {selected && (
        <div
          className={twMerge(
            "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full w-9 h-9",
            "border border-blue-300 dark:border-blue-600 bg-blue-100 dark:bg-blue-900",
          )}
        />
      )}
      <div
        className={twMerge("relative z-10 shrink-0 rounded-full w-7 h-7 m-0.75", colorClassName)}
      />
    </ListboxOption>
  );
}

function ColorItem({ color, currentColor }: { color: Color; currentColor?: Color | null }) {
  return (
    <ColorButton
      value={color}
      selected={currentColor === color}
      colorClassName={twMerge(
        `bg-${color} dark:bg-${mirrorColor(color)}`,
        color === "white" && "border border-gray-200 dark:border-gray-700",
      )}
    />
  );
}

export type ColorType = "text" | "background";

function getColorRows(type: ColorType) {
  return type === "text"
    ? [
        NAMED_COLORS,
        ...COLOR_CATEGORIES.map((color) =>
          COLOR_INTENSITIES.map((intensity) => `${color}-${intensity}` as const),
        ),
      ]
    : [NAMED_COLORS, ...COLOR_CATEGORIES.map((color) => [`${color}-100` as const])];
}

function ColorRows({ type, currentColor }: { type: ColorType; currentColor?: Color | null }) {
  return getColorRows(type).map((items) => (
    <div key={items[0]} className="flex flex-row">
      {items.map((color) => (
        <ColorItem key={color} color={color} currentColor={currentColor} />
      ))}
    </div>
  ));
}

export function ColorGrid({
  type = "text",
  currentColor,
  onSelect,
  className,
}: {
  type?: ColorType;
  currentColor?: Color | null;
  onSelect: (color: Color) => void;
  className?: string;
}) {
  return (
    <Listbox value={currentColor} onChange={onSelect}>
      <ListboxOptions
        static
        className={twMerge(
          "flex flex-col rounded-12 p-2.25 bg-block-bg shadow-md dark:shadow-block-inverted",
          className,
        )}
      >
        <ColorRows type={type} currentColor={currentColor} />
      </ListboxOptions>
    </Listbox>
  );
}

export function ColorIndicator({ color, className }: { color?: Color | null; className?: string }) {
  return (
    <div
      className={twMerge(
        "rounded-full w-6 h-6 p-[3px] bg-document-bg flex items-center justify-center shadow-block dark:shadow-block-inverted",
        className,
      )}
    >
      <div
        className={twMerge(
          "w-full h-full rounded-full",
          `bg-${color || "yellow-100"} dark:bg-${mirrorColor(color || "yellow-900")}`,
        )}
      />
    </div>
  );
}

export type Props = {
  tooltipTitle?: string;
  currentColor?: Color | null;
  onSelect: (colors: Color) => void;
  icon?: React.ReactNode;
  type?: ColorType;
  toggleProps?: ToggleButtonProps;
  className?: string;
  iconClassName?: string;
  hintKey?: HintKey;
};

export function ColorPalette({
  currentColor = "white",
  tooltipTitle = "Color palette",
  icon,
  onSelect,
  type = "text",
  toggleProps,
  className,
  iconClassName,
  hintKey,
}: Props) {
  const createHintHandlers = useCreateHintHoverHandlers();

  const handleSelect = (color: Color) => {
    onSelect(color);
  };

  return (
    <Listbox value={currentColor} onChange={handleSelect}>
      <ListboxButton className={className} aria-label={tooltipTitle} onKeyDown={openListboxOnEnter}>
        <Sidebar.ToggleButton
          as="div"
          tooltipTitle={tooltipTitle}
          // The listbox button around it is the labelled control
          aria-label={undefined}
          icon={
            <div
              className={twMerge(
                "rounded-full w-6 h-6 bg-document-bg flex items-center justify-center shadow-block dark:shadow-block-inverted",
                currentColor && `text-${currentColor} dark:text-${mirrorColor(currentColor)}`,
                iconClassName,
              )}
            >
              {type === "text" ? icon || "A" : <ColorIndicator color={currentColor} />}
            </div>
          }
          {...(hintKey ? createHintHandlers(hintKey) : {})}
          {...toggleProps}
        />
      </ListboxButton>
      <ListboxOptions
        anchor={{ to: "bottom start", padding: "10px" }}
        className={twMerge(
          "relative z-50 flex flex-col rounded-12 p-2.25 bg-block-bg shadow-md dark:shadow-block-inverted outline-none",
        )}
      >
        <ColorRows type={type} currentColor={currentColor} />
      </ListboxOptions>
    </Listbox>
  );
}

export function ColorPickerIcon({
  color,
  className,
}: {
  color?: Color | null;
  className?: string;
}) {
  return (
    <div className="p-[3px] w-full h-full">
      <div
        className={twMerge(
          "w-full h-full rounded-full",
          color && `bg-${color} dark:bg-${mirrorColor(color)}`,
          className,
        )}
      />
    </div>
  );
}
